import { DISCIPLINARY_WEIGHTS } from '../helpers/tarjetasHelper';
import { reportNumber, reportRate, reportSum } from './rivalReport';

const TYPES = [
  ['yellow', 'tarjetasAmarillas'], ['secondYellow', 'segundasAmarillas'], ['red', 'tarjetasRojas'],
];
const SCOPES = ['total', 'home', 'away'];
const VENUES = ['Local', 'Visitante'];
const STATES = ['Ganando', 'Empatando', 'Perdiendo'];
const INTERVALS = ['0_15', '15_30', '30_45', '45_60', '60_75', '75_90'];

const values = fn => Object.fromEntries(SCOPES.map((scope, index) => [scope, fn(scope, index)]));
const pair = (home, away) => ({ total: reportSum(home, away), home, away });

export function teamDisciplineFromPlayers(statistics, players) {
  const fields = TYPES.flatMap(([, prefix]) => [
    ...STATES.flatMap(state => VENUES.map(venue => `${prefix}${state}${venue}`)),
    ...VENUES.flatMap(venue => ['Titular', 'Suplente', ...INTERVALS].map(key => `${prefix}${venue}${key}`)),
  ]);
  const matching = (Array.isArray(players) ? players : []).filter(player =>
    ['idEquipo', 'idCompeticion', 'idGrupo', 'idTemporada'].every(key =>
      statistics?.[key] != null && player?.[key] != null && String(statistics[key]) !== ''
      && String(player[key]) === String(statistics[key])));
  const read = (player, key) => player.tarjetas?.[key] !== undefined ? player.tarjetas[key] : player[key];
  const tarjetas = Object.fromEntries(fields.filter(key => matching.some(player => read(player, key) !== undefined))
    .map(key => [key, reportSum(...matching.map(player =>
      read(player, key) === undefined ? 0 : reportNumber(read(player, key))))]));
  return { ...statistics, tarjetas };
}

function cardSummary(cards, prefix, known, coach = '') {
  const read = key => !known ? null : cards[key] === undefined ? 0 : reportNumber(cards[key]);
  const ownKeys = Object.keys(cards).filter(key => key.startsWith(prefix) && !key.includes('Contrario')
    && key.includes('Entrenador') === Boolean(coach));
  const direct = cards[`${prefix}Totales${coach}`];
  const hasStates = STATES.some(state => VENUES.some(venue => cards[`${prefix}${state}${venue}${coach}`] !== undefined));
  const byVenue = VENUES.map(venue => hasStates || reportNumber(direct) === 0 || !ownKeys.length
    ? reportSum(...STATES.map(state => read(`${prefix}${state}${venue}${coach}`))) : null);
  const total = direct === undefined ? reportSum(...byVenue) : reportNumber(direct);
  const reconciled = total !== null && total === reportSum(...byVenue);
  const counts = { total, home: reconciled ? byVenue[0] : null, away: reconciled ? byVenue[1] : null };
  const hasIntervals = Object.keys(cards).some(key => !key.includes('Contrario')
    && key.includes('Entrenador') === Boolean(coach) && TYPES.some(([, name]) => key.startsWith(name))
    && INTERVALS.some(interval => key.endsWith(interval)));
  const intervals = INTERVALS.map(interval => pair(...VENUES.map((venue, index) =>
    hasIntervals || counts[index === 0 ? 'home' : 'away'] === 0
      ? read(`${prefix}${venue}${coach}${interval}`) : null)));
  const undistributed = values(scope => {
    const sum = reportSum(...intervals.map(row => row[scope]));
    if (counts[scope] === null) return null;
    if (sum === null) return counts[scope];
    if (sum > counts[scope]) {
      intervals.forEach(row => { row[scope] = null; });
      return null;
    }
    return counts[scope] - sum;
  });
  const states = STATES.map(state => pair(...VENUES.map(venue =>
    hasStates || total === 0 ? read(`${prefix}${state}${venue}${coach}`) : null)));
  const hasRoles = ['Titular', 'Suplente'].some(role => VENUES.some(venue => cards[`${prefix}${venue}${role}`] !== undefined));
  const roles = ['Titular', 'Suplente'].map(role => pair(...VENUES.map(venue =>
    hasRoles || total === 0 ? read(`${prefix}${venue}${role}`) : null)));
  return { counts, intervals, undistributed, states, roles };
}

export function teamDisciplineSummary(statistics, selection = 'all') {
  const cards = statistics?.tarjetas ?? statistics ?? {};
  const known = Object.entries(cards).some(([key, value]) => TYPES.some(([, prefix]) => key.startsWith(prefix))
    && reportNumber(value) !== null);
  const types = TYPES.map(([key, prefix]) => ({ key, ...cardSummary(cards, prefix, known) }));
  const selected = types.filter(type => selection === 'all' || type.key === selection);
  const combine = getter => values(scope => reportSum(...selected.map(type => getter(type)[scope])));
  const counts = types.map(type => ({ key: type.key, values: type.counts }));
  const total = values(scope => reportSum(...types.map(type => type.counts[scope])));
  const points = values(scope => reportSum(...types.map(type => type.counts[scope] === null ? null
    : type.counts[scope] * DISCIPLINARY_WEIGHTS[type.key])));
  const played = values((scope, index) => reportNumber(statistics?.partidos?.[
    ['partidosJugados', 'partidosJugadosLocal', 'partidosJugadosVisitante'][index]
  ]));
  return {
    hasData: known, played,
    counts: [...counts, { key: 'totalCards', values: total }, { key: 'points', values: points }],
    rates: [...counts, { key: 'totalCards', values: total }].map(row => ({
      ...row, values: values(scope => reportRate(row.values[scope], played[scope])),
    })),
    intervals: [...INTERVALS.map((key, index) => ({ key, values: combine(type => type.intervals[index]) })),
      { key: 'unassigned', values: combine(type => type.undistributed) }],
    states: STATES.map((key, index) => ({ key, values: combine(type => type.states[index]) })),
    roles: ['Titular', 'Suplente'].map((key, index) => ({ key, values: combine(type => type.roles[index]) })),
    coaches: TYPES.map(([key, prefix]) => ({ key, values: cardSummary(cards, prefix, known, 'Entrenador').counts })),
    hasCoaches: Object.keys(cards).some(key => key.includes('Entrenador') && !key.includes('Contrario')),
  };
}
